import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import { PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { api } from '../config';

const Modules = () => {
  const theme = useTheme();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningModule, setRunningModule] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchModules = async () => {
    try {
      console.log('Fetching modules');
      const response = await api.get('/modules');
      setModules(response.data);
      setError(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error fetching modules: ' + error.message,
        severity: 'error'
      });
      console.error('Error fetching modules:', error);
      setError('Failed to fetch modules. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleRunModule = async (moduleId) => {
    setRunningModule(moduleId);
    try {
      const response = await api.post(`/modules/${moduleId}/run`);
      setSnackbar({
        open: true,
        message: response.data.message || 'Module executed successfully',
        severity: 'success'
      });
      // Refresh modules to update last run time
      fetchModules();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error running module: ' + error.message,
        severity: 'error'
      });
    } finally {
      setRunningModule(null);
    }
  };

  const getLastRunStatus = (module) => {
    if (!module.last_run) {
      return { status: 'Never', color: 'default', icon: null };
    }
    
    // Check if there's a last_run_success field
    if (module.last_run_success === true) {
      return { status: 'Success', color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5 }} /> };
    } else if (module.last_run_success === false) {
      return { status: 'Failed', color: 'error', icon: <ErrorIcon sx={{ fontSize: 18, mr: 0.5 }} /> };
    }
    
    return { status: module.last_run, color: 'default', icon: null };
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Modules
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Module ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Run</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Result</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((module) => {
              const statusInfo = getLastRunStatus(module);
              return (
                <TableRow key={module.id} hover>
                  <TableCell>{module.id}</TableCell>
                  <TableCell>{module.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={module.is_running ? 'Running' : 'Stopped'}
                      color={module.is_running ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {module.last_run ? new Date(module.last_run).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    {statusInfo.status === 'Never' ? (
                      <Typography variant="body2" color="textSecondary">Never run</Typography>
                    ) : (
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.status}
                        color={statusInfo.color}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={runningModule === module.id ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                      onClick={() => handleRunModule(module.id)}
                      disabled={runningModule === module.id}
                      size="small"
                    >
                      Run Now
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Modules;